(() => {
  'use strict';

  const CONFIG_ID = 'BmhComboCroConfig';
  const ROOT_CLASS = 'bmh-combo-cro-ready';
  const REVEAL_CLASS = 'bmh-combo-reveal';
  const VISIBLE_CLASS = 'is-visible';
  let observer;
  let galleryController;
  let warmTimer;

  const prepareGallery = (config) => {
    galleryController?.abort();
    clearTimeout(warmTimer);
    const gallery = document.querySelector('.product-information__media slideshow-component');
    if (!gallery) return;
    galleryController = new AbortController();
    const { signal } = galleryController;
    const media = gallery.closest('.product-information__media');
    media.classList.add('bmh-product-gallery');
    gallery.classList.add('bmh-product-gallery__slideshow');
    const slides = [...gallery.querySelectorAll('slideshow-slide')];
    let playTimer;
    let playVersion = 0;
    slides.forEach((slide, index) => {
      if (!slide.classList.contains('product-media-container--zoomable')) return;
      slide.tabIndex = 0;
      slide.setAttribute('role', 'button');
      const description = slide.querySelector('img')?.alt || '';
      const label = config.zoomMediaLabel?.replace('__index__', String(index + 1));
      slide.setAttribute('aria-label', label ? `${label}: ${description}` : description);
      slide.addEventListener('keydown', event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        slide.click();
      }, { signal });
    });
    const syncVideo = () => {
      clearTimeout(playTimer);
      const version = ++playVersion;
      slides.forEach(slide => {
        if (document.hidden || slide.getAttribute('aria-hidden') === 'true') {
          slide.querySelectorAll('video').forEach(video => video.pause());
        }
      });
      if (document.hidden) return;
      const active = slides.find(slide => slide.getAttribute('aria-hidden') !== 'true');
      if (!active || active.dataset.bmhVideoPlayed === 'true' || !active.querySelector('deferred-media, video')) return;
      active.dataset.bmhVideoPlayed = 'true';
      active.querySelector('deferred-media button, .deferred-media__poster-button')?.click();
      playTimer = setTimeout(() => {
        if (signal.aborted || version !== playVersion || document.hidden || active.getAttribute('aria-hidden') === 'true') return;
        const video = active.querySelector('video');
        if (!video) return;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = false;
        video.play().then(() => {
          if (signal.aborted || version !== playVersion || document.hidden || active.getAttribute('aria-hidden') === 'true') video.pause();
        }).catch(() => {});
      }, 80);
    };
    const videosObserver = new MutationObserver(syncVideo);
    videosObserver.observe(gallery, { attributes: true, subtree: true, attributeFilter: ['aria-hidden'] });
    document.addEventListener('visibilitychange', syncVideo, { signal });
    signal.addEventListener('abort', () => { videosObserver.disconnect(); clearTimeout(playTimer); playVersion++; }, { once: true });
    syncVideo();
    const warm = (index) => {
      if (document.hidden || navigator.connection?.saveData) return;
      for (const offset of [-1, 1]) {
        const image = slides[index + offset]?.querySelector('.product-media__image');
        if (image) { image.fetchPriority = 'low'; image.loading = 'eager'; }
      }
    };
    gallery.addEventListener('slideshow:select', (event) => {
      if (event.target !== gallery) return;
      clearTimeout(warmTimer);
      warmTimer = setTimeout(() => warm(event.detail.index), 200);
    }, { signal });
  };

  const sectionSelectors = [
    "[id$='__blocks_KKbcDt']",
    "[id$='__blocks_femFLh']",
    "[id$='__178113937855300905']",
    "[id$='__17811420468ec0e0c0']",
    "[id$='__1780887824e8495511']",
    "[id$='__product_recommendations_nRYUDy']"
  ];

  const clamp = (value, min, max, fallback) => {
    const number = Number(value);
    return Number.isFinite(number)
      ? Math.min(max, Math.max(min, number))
      : fallback;
  };

  const readConfig = () => {
    const element = document.getElementById(CONFIG_ID);
    if (!element) return null;

    try {
      const data = JSON.parse(element.textContent || '{}');
      return {
        enabled: data.enabled !== false,
        zoomMediaLabel: data.zoom_media,
        duration: clamp(data.duration, 500, 2000, 800),
        stagger: clamp(data.stagger, 100, 1000, 150),
        triggerOffset: clamp(data.triggerOffset, 0, 300, 80)
      };
    } catch (error) {
      console.warn('[BMH Combo CRO] No se pudo leer la configuración.', error);
      return {
        enabled: true,
        duration: 800,
        stagger: 150,
        triggerOffset: 80
      };
    }
  };

  const getRevealItems = (section) => {
    if (section.id.endsWith('__blocks_KKbcDt')) {
      return Array.from(section.querySelectorAll('a')).slice(0, 4);
    }

    if (section.id.endsWith('__blocks_femFLh')) {
      return Array.from(
        section.querySelectorAll("[class*='ai-sunrise-gallery-card-']")
      ).slice(0, 3);
    }

    if (section.id.endsWith('__17811420468ec0e0c0')) {
      return Array.from(section.querySelectorAll('a')).slice(0, 4);
    }

    if (section.id.endsWith('__product_recommendations_nRYUDy')) {
      return Array.from(
        section.querySelectorAll('.product-card, product-card')
      ).slice(0, 4);
    }

    return [];
  };

  const revealImmediately = (sections) => {
    sections.forEach((section) => {
      section.classList.add(VISIBLE_CLASS);
      getRevealItems(section).forEach((item) => {
        item.style.removeProperty('--bmh-combo-item-delay');
      });
    });
  };

  const initialize = () => {
    const config = readConfig();
    if (!config) return;
    observer?.disconnect();
    prepareGallery(config);

    const root = document.documentElement;
    const sections = sectionSelectors.flatMap((selector) =>
      Array.from(document.querySelectorAll(selector))
    );

    if (!sections.length) return;

    document.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.decoding = 'async';
    });

    if (!config.enabled) {
      root.classList.remove(ROOT_CLASS);
      sections.forEach((section) => {
        section.classList.remove(REVEAL_CLASS, VISIBLE_CLASS);
        delete section.dataset.bmhCroPrepared;
      });
      return;
    }

    root.style.setProperty(
      '--bmh-combo-reveal-duration',
      `${config.duration}ms`
    );
    root.style.setProperty(
      '--bmh-combo-stagger',
      `${config.stagger}ms`
    );

    sections.forEach((section) => {
      if (section.dataset.bmhCroPrepared === 'true') return;

      section.dataset.bmhCroPrepared = 'true';
      section.classList.add(REVEAL_CLASS);

      getRevealItems(section).forEach((item, index) => {
        item.classList.add('bmh-combo-reveal-item');
        item.style.setProperty(
          '--bmh-combo-item-delay',
          `${index * config.stagger}ms`
        );
      });
    });

    const preparedSections = sections.filter(
      (section) => !section.classList.contains(VISIBLE_CLASS)
    );

    const reduceMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (
      reduceMotion ||
      !('IntersectionObserver' in window) ||
      !preparedSections.length
    ) {
      root.classList.add(ROOT_CLASS);
      revealImmediately(sections);
      return;
    }

    root.classList.add(ROOT_CLASS);

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          observer.unobserve(entry.target);

          entry.target.classList.add(VISIBLE_CLASS);
        });
      },
      {
        threshold: 0,
        rootMargin: `0px 0px ${Math.min(config.triggerOffset, 80)}px 0px`
      }
    );

    preparedSections.forEach((section) => observer.observe(section));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', (event) => {
    observer?.unobserve(event.target);
    if (event.target.querySelector('.product-information')) {
      galleryController?.abort();
      clearTimeout(warmTimer);
    }
  });
})();
